import React, { useMemo } from "react";
import { connect } from "react-redux";
import ShiftAttendance from "./components/ShiftAttendance";
import ZoneGrid from "./components/ZoneGrid";
import OperationsSnapshot from "./components/OperationsSnapshot";
import DateRangeFilter, { OPTIONS as DATE_RANGE_OPTIONS } from "./components/DateRangeFilter";
import CustomDateRange from "./components/CustomDateRange";
import { sumProceduresInRange } from "../../Utils/counts";
import "./Overview.css";

function Overview({ locationsData, graphSummaryData, complaintsCount, workforceLoading, accessToken, allowedZones, className, range, onRangeChange, onZoneClick, onComplaintsClick, onProceduresClick, onWorkforceClick }) {
  const rangeLabel = DATE_RANGE_OPTIONS.find((opt) => opt.key === range.type)?.label || "";

  const zoneStats = useMemo(() => {
    if (!locationsData) return [];
    const byZone = {};
    Object.values(locationsData).forEach((loc) => {
      const z = loc.zone;
      if (!z) return;
      if (!byZone[z]) byZone[z] = { total: 0, active: 0, takenOver: 0, takenOverAndActive: 0, openComplaints: 0 };
      byZone[z].total += 1;
      if (loc.operationalStatus?.state === "Active") byZone[z].active += 1;
      if (loc.takeoverTime != null) byZone[z].takenOver += 1;
      if (loc.takeoverTime != null && loc.operationalStatus?.state === "Active") byZone[z].takenOverAndActive += 1;
      byZone[z].openComplaints += loc.totalOpenCompliant || 0;
    });
    return Object.entries(byZone)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([zone, data]) => ({
        id: zone,
        total: data.total,
        active: data.active,
        takenOver: data.takenOver,
        completionPct: Math.round((data.takenOverAndActive / data.total) * 100),
        takenOverAndActive: data.takenOverAndActive,
        openComplaints: data.openComplaints
      }));
  }, [locationsData]);

  const stateMap = useMemo(() => {
    if (!locationsData) return null;
    const counts = {};
    Object.values(locationsData).forEach((l) => {
      const state = l.operationalStatus?.state || "Unknown";
      counts[state] = (counts[state] || 0) + 1;
    });
    return counts;
  }, [locationsData]);

  const proceduresData = sumProceduresInRange(Object.values(locationsData || {}), range.from, range.to);

  const { present = 0, absent = 0 } = graphSummaryData?.employeeAttendance || {};

  const todayLabel = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <div className={className || "screen active"} data-cy="exec-overview">
      <div className="scroll">
        <div style={{ padding: "12px 20px 14px" }}>
          <div className="t-page" data-cy="overview-city-title">Chennai</div>
          <div style={{ marginTop: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "var(--text-base)", color: "var(--t2)" }}>All Zones</span>
          </div>
        </div>

        <div className="scope-bar" data-cy="scope-bar-live">
          <span className="scope-bar-title">Today's Overview</span>
          <span className="scope-bar-note">{todayLabel}</span>
        </div>

        <ShiftAttendance stateMap={stateMap} accessToken={accessToken} allowedZones={allowedZones} />

        <ZoneGrid zones={zoneStats} onZoneClick={onZoneClick} />

        <div className="filter-stack" data-cy="filter-stack">
          <div className="scope-bar">
            <span className="scope-bar-title">Operations</span>
            <DateRangeFilter value={range.type} onRangeChange={onRangeChange} />
          </div>
          {range.type === "custom" && <CustomDateRange onRangeChange={onRangeChange} />}
        </div>

        <OperationsSnapshot
          complaintsData={complaintsCount}
          workforceData={{ present, absent }}
          workforceLoading={workforceLoading}
          rangeLabel={rangeLabel}
          proceduresData={proceduresData}
          onComplaintsClick={onComplaintsClick}
          onProceduresClick={onProceduresClick}
          onWorkforceClick={onWorkforceClick}
        />

        <div style={{ height: "20px" }} />
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  locationsData: state.locations.data,
  graphSummaryData: state.locations.graphSummaryData,
  complaintsCount: state.complaints.complaintsCount,
  workforceLoading: state.locations.workforceLoading,
  accessToken: state.auth.data.accessToken,
  allowedZones: state.auth.data.zones,
});

export default connect(mapStateToProps)(Overview);
